<template>
  <div>
    <!-- Hero -->
    <section class="home-hero">
      <picture>
        <source media="(max-width: 900px)" srcset="/images/00-home-hero-mobile.png" />
        <img class="home-hero-image" src="/images/00-home-hero-desktop.png" :alt="t('home.heroAlt')" />
      </picture>
      <div class="home-hero-copy">
        <h1>{{ t('home.heroTitle') }}</h1>
        <p class="hero-slogan">CRAFTED FOR WHAT MATTERS</p>
      </div>
    </section>

    <!-- 五大产品族 -->
    <section class="section family-section">
      <div class="family-images">
        <router-link
          v-for="(cat, idx) in categories"
          :key="cat.id"
          :to="'/products/' + cat.slug"
          class="family-image-card"
          :style="idx === 4 ? 'grid-column: 1 / -1' : ''"
        >
          <picture v-if="mobileImageOf(cat)">
            <source media="(max-width: 900px)" :srcset="mobileImageOf(cat)" />
            <img :src="cat.image" :alt="tf(cat, 'name')" />
          </picture>
          <img v-else :src="cat.image" :alt="tf(cat, 'name')" />
          <div class="family-copy">
            <h3>{{ tf(cat, 'name') }}</h3>
            <p v-if="tf(cat, 'summary')" class="family-summary">{{ tf(cat, 'summary') }}</p>
            <span class="family-pill">{{ idx === 4 ? t('home.familyExploreCustom') : t('home.heroExplore') }}</span>
          </div>
        </router-link>
      </div>
    </section>

    <!-- 品牌理念 -->
    <section class="about-home">
      <div class="about-mark" aria-hidden="true">SN</div>
      <div>
        <p class="eyebrow">ABOUT SCOTT NOLES</p>
        <h2>{{ t('home.aboutTitle') }}</h2>
        <p>{{ t('home.aboutBody') }}</p>
        <router-link to="/about" class="text-link">{{ t('home.aboutLink') }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
      </div>
    </section>

    <!-- 洞察与新闻 -->
    <section class="section" style="padding-top: 90px; padding-bottom: 100px;">
      <div class="section-title-row">
        <div>
          <p class="eyebrow">INSIGHTS &amp; NEWS</p>
          <h2>{{ t('home.insightsTitle') }}</h2>
        </div>
        <router-link to="/insights" class="text-link">{{ t('common.more') }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
      </div>
      <div class="article-rows">
        <router-link v-for="a in articles.slice(0, 2)" :key="a.id" :to="'/insights/' + (a.slug || a.id)" class="article-row">
          <div class="article-row-meta">
            <span>{{ articleTypeLabel(a.type) }}</span>
            <time :datetime="a.publishDate || a.date">{{ formatDate(a.publishDate || a.date) }}</time>
            <span>{{ tf(a, 'readTime') }}</span>
          </div>
          <div>
            <h2>{{ tf(a, 'title') }}</h2>
            <p>{{ tf(a, 'excerpt') }}</p>
            <span class="article-read">{{ t('common.readArticle') }}
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
            </span>
          </div>
        </router-link>
      </div>
    </section>

    <!-- 服务 -->
    <section class="section" style="padding-bottom: 80px;">
      <div class="section-title-row">
        <div>
          <p class="eyebrow">SERVICE</p>
          <h2>{{ t('home.serviceTitle') }}</h2>
        </div>
      </div>
      <div class="service-hub-links">
        <router-link to="/warranty/query" class="service-card">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>
          <div>
            <h2>{{ t('home.serviceWarrantyTitle') }}</h2>
            <p>{{ t('home.serviceWarrantyDesc') }}</p>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
        <router-link to="/warranty/authentication" class="service-card">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>
          <div>
            <h2>{{ t('home.serviceAuthTitle') }}</h2>
            <p>{{ t('home.serviceAuthDesc') }}</p>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
      </div>
      <div class="support-note">
        <span style="color:#666c71;font-size:.88rem;">{{ t('home.serviceHelp') }}</span>
        <a :href="'mailto:' + brand.supportEmail">{{ brand.supportEmail }}</a>
        <a :href="'tel:' + brand.telephone">{{ brand.telephone }}</a>
      </div>
    </section>

    <!-- 联系区块 -->
    <section class="contact-home">
      <div>
        <p class="eyebrow">CONTACT</p>
        <h2>{{ t('home.contactTitle1') }}<br />{{ t('home.contactTitle2') }}</h2>
      </div>
      <div class="contact-home-links">
        <router-link to="/contact">
          <span>01</span>
          <div>
            <h3>{{ t('home.contactBook') }}</h3>
            <p>{{ t('home.contactBookSub') }}</p>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
        <router-link to="/contact">
          <span>02</span>
          <div>
            <h3>{{ t('home.contactSales') }}</h3>
            <p>{{ t('home.contactSalesSub') }}</p>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
        <router-link :to="{ path: '/contact', query: { topic: 'support' } }">
          <span>03</span>
          <div>
            <h3>{{ t('home.contactSupport') }}</h3>
            <p>{{ brand.supportEmail }}</p>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
import { getOverview, getArticles } from '../api/content'
import { langMixin } from '../utils/site-lang'
import {
  categories as fbCategories,
  articles as fbArticles,
  brand
} from '../data/site-data'

export default {
  name: 'HomeView',
  mixins: [langMixin],
  data () {
    return {
      // 初始用静态数据兜底，接口返回后覆盖
      categories: fbCategories,
      articles: fbArticles,
      brand
    }
  },
  created () {
    this.loadAll()
  },
  methods: {
    async loadAll () {
      try {
        const overview = await getOverview()
        if (overview && overview.categories) {
          this.categories = overview.categories
        }
      } catch (e) { /* 静态兜底 */ }
      try {
        const res = await getArticles({ page: 1, size: 10 })
        if (res && Array.isArray(res.list)) this.articles = res.list
      } catch (e) { /* 静态兜底 */ }
    },
    // 首页产品族/首页大图移动端竖版图（约定派生）：
    // 1) 0X-xxx-desktop.png 命名：对应 -mobile.png
    // 2) /images/home-family-* 旧 webp：同名加 -mobile 后缀；
    // 非本站图片（如后台上传）不派生，移动端沿用原图
    mobileImageOf (cat) {
      const img = cat.image || ''
      if (img.includes('-desktop.') && !img.includes('-mobile.')) {
        return img.replace('-desktop.', '-mobile.')
      }
      if (!img.startsWith('/images/home-family-') || img.includes('-mobile.')) return null
      return img.replace(/(\.webp|\.png|\.jpg|\.jpeg)$/i, '-mobile$1')
    },
    formatDate (d) {
      if (!d) return ''
      const dt = new Date(d)
      if (this.lang === 'en') {
        return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      }
      return `${dt.getFullYear()}年${dt.getMonth() + 1}月${dt.getDate()}日`
    }
  }
}
</script>
